import { Vector3 } from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import type { BufferGeometry } from 'three';
import type { VoxelGrid } from './VoxelGrid';

/**
 * Deliberate interproximal blockout: fills the NARROWEST interdental gaps
 * with a smooth web, on the RAW solid SDF — BEFORE the hollow-shell slab
 * transform (`applySheetSlab`).
 *
 * Rationale (thermoformed foil): the foil cannot pull into gaps narrower than
 * ~a millimetre anyway, and gaps near the voxel size come out of marching
 * cubes as ragged lace — neither cleanly open nor cleanly closed, and the
 * foil wrinkles on it. So the model closes them ON PURPOSE: anywhere two
 * neighbouring crowns run closer than `maxGapMm`, the gap is bridged; wider
 * embrasures keep their original scanned shape.
 *
 * The filled solid is the GAP FIELD {dA + dB ≤ maxGapMm}: dA + dB measures
 * the LOCAL WIDTH of the gap a point sits in (it equals the surface-to-surface
 * distance exactly on the segment between the two closest points), so material
 * appears strictly BETWEEN the two crowns and only while that width is under
 * the threshold. Crucially, a point ON either crown's surface has dA = 0 and
 * is filled only when the other crown is nearer than the threshold — the web
 * ends AT the surfaces and can never push them outward.
 *
 * That is the whole reason this is not a smooth-union (smin) of the two
 * crowns: smin adds material wherever the two distances are merely SIMILAR,
 * which on the broad, near-parallel proximal walls of real crowns inflates
 * the surface by up to k/4 — visible as fattened contacts and rounded-off
 * embrasure mouths from the buccal, lingual and occlusal sides alike.
 *
 * `weldMm` rounds the concave junction where the web meets each crown with a
 * small tangent fillet (a sharp valley there stair-steps at voxel scale). It
 * is the ONLY term that adds material outside the gap field, and it is capped
 * at weldMm/4 — keep it sub-voxel.
 *
 * Returns the number of voxels the web turned from air into material.
 */
export function blockoutInterproximalWebs(
  grid: VoxelGrid,
  teeth: BufferGeometry[],
  maxGapMm = 0.6,
  weldMm = 0.2,
): number {
  if (teeth.length < 2 || maxGapMm <= 0) return 0;
  const trees = teeth.map((geometry) => {
    geometry.computeBoundingBox();
    return { bvh: new MeshBVH(geometry), box: geometry.boundingBox! };
  });

  const { origin, counts, strides, cellSize } = grid.info;
  const data = grid.data;
  const point = new Vector3();
  const hit = { point: new Vector3(), distance: 0, faceIndex: 0 };
  const target = hit as Parameters<MeshBVH['closestPointToPoint']>[1];

  const distanceWithin = (
    tree: { bvh: MeshBVH; box: { min: Vector3; max: Vector3 } },
    maxDist: number,
  ): number => {
    const dx = Math.max(tree.box.min.x - point.x, 0, point.x - tree.box.max.x);
    const dy = Math.max(tree.box.min.y - point.y, 0, point.y - tree.box.max.y);
    const dz = Math.max(tree.box.min.z - point.z, 0, point.z - tree.box.max.z);
    if (dx * dx + dy * dy + dz * dz >= maxDist * maxDist) return Infinity;
    const found = tree.bvh.closestPointToPoint(point, target, 0, maxDist);
    return found ? hit.distance : Infinity;
  };

  // The web's own field is written a few cells PAST its surface too, so
  // marching cubes interpolates the web boundary off real values instead of
  // snapping it to a cell edge (the source of stair-stepped rims).
  const maxSum = maxGapMm + 8 * cellSize;
  // Deeper than this no update can reach: the gap field bottoms out at
  // −maxGap/2 (mid-gap of a zero-width gap) and the weld adds ≤ weld/4.
  const skipBelow = -(maxGapMm / 2 + weldMm);

  let filled = 0;
  for (let i = 0; i < trees.length; i++) {
    for (let j = i + 1; j < trees.length; j++) {
      const minX = Math.max(trees[i].box.min.x, trees[j].box.min.x) - maxSum;
      const maxX = Math.min(trees[i].box.max.x, trees[j].box.max.x) + maxSum;
      const minY = Math.max(trees[i].box.min.y, trees[j].box.min.y) - maxSum;
      const maxY = Math.min(trees[i].box.max.y, trees[j].box.max.y) + maxSum;
      const minZ = Math.max(trees[i].box.min.z, trees[j].box.min.z) - maxSum;
      const maxZ = Math.min(trees[i].box.max.z, trees[j].box.max.z) + maxSum;
      if (minX > maxX || minY > maxY || minZ > maxZ) continue;
      const x0 = Math.max(0, Math.floor((minX - origin.x) / cellSize));
      const x1 = Math.min(counts.x - 1, Math.ceil((maxX - origin.x) / cellSize));
      const y0 = Math.max(0, Math.floor((minY - origin.y) / cellSize));
      const y1 = Math.min(counts.y - 1, Math.ceil((maxY - origin.y) / cellSize));
      const z0 = Math.max(0, Math.floor((minZ - origin.z) / cellSize));
      const z1 = Math.min(counts.z - 1, Math.ceil((maxZ - origin.z) / cellSize));

      for (let x = x0; x <= x1; x++) {
        for (let y = y0; y <= y1; y++) {
          let idx = x * strides.x + y * strides.y + z0 * strides.z;
          for (let z = z0; z <= z1; z++, idx += strides.z) {
            const current = data[idx];
            if (current <= skipBelow) continue;
            point.set(
              origin.x + x * cellSize,
              origin.y + y * cellSize,
              origin.z + z * cellSize,
            );
            const dA = distanceWithin(trees[i], maxSum);
            if (dA === Infinity) continue;
            const remaining = maxSum - dA;
            if (remaining <= 0) continue;
            const dB = distanceWithin(trees[j], remaining);
            if (dB === Infinity) continue;
            // Local gap width vs threshold. Halved: dA + dB changes at up to
            // 2 per mm, and marching cubes lerps the value as a distance.
            const web = (dA + dB - maxGapMm) / 2;
            let next = Math.min(current, web);
            if (weldMm > 0) {
              // Quadratic smooth-min ONLY around the junction (|current − web|
              // < weld); on open walls `web` is far larger and nothing moves.
              const h = Math.max(weldMm - Math.abs(current - web), 0) / weldMm;
              next -= h * h * weldMm * 0.25;
            }
            if (next >= current) continue;
            data[idx] = next;
            if (current >= 0 && next < 0) filled++;
          }
        }
      }
    }
  }
  return filled;
}
